import { Children, Fragment, cloneElement } from "react";
import type { CSSProperties, ReactElement, ReactNode } from "react";

type Margin = CSSProperties["margin"];

/** Margin, named after the Tailwind utilities. A number is pixels; a string is
 * any CSS margin value, e.g. `"auto"` or `"1rem"`. The more specific prop
 * wins: `<Spacing m={8} mt={0} />` applies 8px exterior margins on every
 * side except the top.
 */
type Space = {
  m?: Margin;
  mx?: Margin;
  my?: Margin;
  mt?: Margin;
  mr?: Margin;
  mb?: Margin;
  ml?: Margin;
};

/** Put the margins on the only child instead of on a wrapper `div`. Use it when
 * a wrapper would change the layout — an inline element, a flex or grid item —
 * or to replace a margin the child sets itself, which a wrapper adds to rather
 * than overrides.
 */
type AsChild =
  | { asChild: true; children: ReactElement<{ style?: CSSProperties }> }
  | { asChild?: false; children?: ReactNode };

export type SpacingProps = Space & AsChild;

/** Omits the sides that were not given, so merging never clears a child's own margin. */
const toMargin = ({ m, mx, my, mt, mr, mb, ml }: Space): CSSProperties => {
  const [top, right, bottom, left] = [mt ?? my, mr ?? mx, mb ?? my, ml ?? mx];
  return {
    ...(m !== undefined && { margin: m }),
    ...(top !== undefined && { marginTop: top }),
    ...(right !== undefined && { marginRight: right }),
    ...(bottom !== undefined && { marginBottom: bottom }),
    ...(left !== undefined && { marginLeft: left }),
  };
};

export const Spacing = (props: SpacingProps) => {
  const margin = toMargin(props);
  if (!props.asChild) return <div style={margin}>{props.children}</div>;
  const child = Children.only(props.children);
  if (child.type === Fragment)
    throw new Error("<Spacing asChild> needs an element child, not a fragment");
  return cloneElement(child, {
    style: { ...child.props.style, ...margin },
  });
};
